import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

/**
 * Hook to subscribe to real-time connection events and show toast notifications
 */
export function useConnectionNotifications() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const processedEvents = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!user?.id) return;

    const channel = supabase
      .channel("connection-notifications")
      // Listen for new connection requests where I'm the recipient
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "connections",
          filter: `recipient_id=eq.${user.id}`,
        },
        async (payload) => {
          const connection = payload.new as any;
          
          // Prevent duplicate toasts
          const eventKey = `request-${connection.id}`;
          if (processedEvents.current.has(eventKey)) return;
          processedEvents.current.add(eventKey);
          
          if (connection.status === "pending") {
            // Fetch requester name
            const { data: requester } = await supabase
              .from("profiles")
              .select("full_name")
              .eq("id", connection.requester_id)
              .single();

            const requesterName = requester?.full_name || "Someone";

            toast.info(`${requesterName} wants to connect with you`, {
              action: {
                label: "View Request",
                onClick: () => navigate("/connections"),
              },
              duration: 8000,
            });

            // Invalidate queries
            queryClient.invalidateQueries({ queryKey: ["received_connection_requests"] });
            queryClient.invalidateQueries({ queryKey: ["community_connection"] });
          }
        }
      )
      // Listen for updates to connections where I'm the requester (acceptance/decline)
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "connections",
          filter: `requester_id=eq.${user.id}`,
        },
        async (payload) => {
          const oldConnection = payload.old as any;
          const newConnection = payload.new as any;
          
          // Only handle status changes from pending
          if (oldConnection.status !== "pending") return;
          
          // Prevent duplicate toasts
          const eventKey = `update-${newConnection.id}-${newConnection.status}`;
          if (processedEvents.current.has(eventKey)) return;
          processedEvents.current.add(eventKey);

          // Fetch recipient name
          const { data: recipient } = await supabase
            .from("profiles")
            .select("full_name")
            .eq("id", newConnection.recipient_id)
            .single();

          const recipientName = recipient?.full_name || "Someone";

          if (newConnection.status === "accepted") {
            toast.success(`🎉 ${recipientName} accepted your connection request!`, {
              action: {
                label: "View",
                onClick: () => navigate("/connections"),
              },
              duration: 8000,
            });
          } else if (newConnection.status === "declined") {
            toast.info("Your connection request was not accepted", {
              duration: 5000,
            });
          }

          // Invalidate queries
          queryClient.invalidateQueries({ queryKey: ["sent_connection_requests"] });
          queryClient.invalidateQueries({ queryKey: ["my_connections"] });
          queryClient.invalidateQueries({ queryKey: ["community_connection"] });
        }
      )
      // Listen for updates where I'm the recipient (for realtime UI updates)
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "connections",
          filter: `recipient_id=eq.${user.id}`,
        },
        () => {
          // Invalidate queries for UI updates
          queryClient.invalidateQueries({ queryKey: ["received_connection_requests"] });
          queryClient.invalidateQueries({ queryKey: ["my_connections"] });
          queryClient.invalidateQueries({ queryKey: ["community_connection"] });
        }
      )
      // Listen for deletions (cancelled or removed connections)
      .on(
        "postgres_changes",
        {
          event: "DELETE",
          schema: "public",
          table: "connections",
        },
        (payload) => {
          const deleted = payload.old as any;
          
          // Only invalidate if I was part of this connection
          if (deleted.requester_id === user.id || deleted.recipient_id === user.id) {
            queryClient.invalidateQueries({ queryKey: ["sent_connection_requests"] });
            queryClient.invalidateQueries({ queryKey: ["received_connection_requests"] });
            queryClient.invalidateQueries({ queryKey: ["my_connections"] });
            queryClient.invalidateQueries({ queryKey: ["community_connection"] });
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id, queryClient, navigate]);
}
